import { Card, Tabs, Tab, Form } from "react-bootstrap";
import CustomButton from "../../components/CustomButton/CustomButton";
import { useEffect, useMemo, useRef, useState } from "react";
import WebService from "../../utility/WebService";
import { PencilFill, TrashFill } from "react-bootstrap-icons";
import DeleteModal from "../../components/DeleteModal/DeleteModal";
import AddLeadershipCouncil from "./AddLeadershipCouncil";
import { toast } from "react-toastify";
import CustomTableSortable from "../../components/CustomTable/CustomTableSortable";

type Leader = {
  _id: string;
  name: string;
  designation: string;
  company: string;
  image: string;
  year: number;
  order_by?: number;
};

const LeadershipCouncil = () => {
  const [showAddLeadershipCouncil, setShowAddLeadershipCouncil] =
    useState(false);
  const [leadershipCouncils, setLeadershipCouncils] = useState<any[]>([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [leadershipCouncilToDelete, setLeadershipCouncilToDelete] = useState<
    string | null
  >(null);
  const [leadershipCouncilToEdit, setLeadershipCouncilToEdit] =
    useState<Leader | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const [search, setSearch] = useState("");
  const searchRef = useRef<string>("");

  const [pagePermission, setPagePermission] = useState({
    canView: false,
    canAdd: false,
    canEdit: false,
    canDelete: false,
  });

  const [yearWiseLeaders, setYearWiseLeaders] =
    useState<Record<string, Leader[]>>({});

  /* ================= GET YEAR WISE LEADERS ================= */
  const getLeadersByYear = () => {
    WebService.getAPI({
      action: "leadership/getLeadersByYear",
    }).then((res: any) => {
      console.log(res, "<<<")
      setYearWiseLeaders(res?.data || {});
    });
  };

  /* ================= YEAR KEYS (SAFE) ================= */
  const yearKeys = useMemo(() => {
    return Object.keys(yearWiseLeaders || {}).sort(
      (a, b) => Number(b) - Number(a)
    );
  }, [yearWiseLeaders]);

  console.log(yearWiseLeaders, ">>>")
  console.log(yearKeys, "???")

  /* ================= GET TABLE DATA ================= */
  const getLeadershipCouncils = (keyword: string = "") => {
    WebService.getAPI({
      action: `leadership/getLeaders?page=${currentPage}&limit=${itemsPerPage}&keyword=${keyword}`,
    })
      .then((res: any) => {
        const perm = res?.data?.permissions || {};

        setPagePermission({
          canView: perm.canView,
          canAdd: perm.canAdd,
          canEdit: perm.canEdit,
          canDelete: perm.canDelete,
        });

        const rows = (res?.data?.data || []).map((row: any) => ({
          ...row,
          originalImage: row.image,
          image: (
            <img
              src={row.image}
              alt={row.name}
              style={{
                width: "80px",
                height: "80px",
                borderRadius: "50%",
                objectFit: "cover",
              }}
            />
          ),
          action: (
            <div>
              {perm.canEdit && (
                <PencilFill
                  className="me-3 cursor-pointer"
                  onClick={() => {
                    setLeadershipCouncilToEdit(row);
                    setShowAddLeadershipCouncil(true);
                  }}
                />
              )}

              {perm.canDelete && (
                <TrashFill
                  color="#DC143C"
                  className="cursor-pointer"
                  onClick={() => handleDeleteEvent(row._id)}
                />
              )}
            </div>
          ),
        }));

        setLeadershipCouncils(rows);
        setTotalCount(res?.data?.pagination?.total || 0);
      })
      .catch(() => { });
  };

  useEffect(() => {
    getLeadersByYear();
  }, []);

  useEffect(() => {
    getLeadershipCouncils(searchRef.current);
  }, [currentPage]);

  /* ================= REORDER ================= */
  const handleReorder = (newRows: any[]) => {
    setLeadershipCouncils(newRows);

    const payload = newRows.map((item: any, index: number) => ({
      _id: item._id,
      order_by: index + 1,
    }));

    WebService.postAPI({
      action: "leadership/update-order",
      body: payload,
    }).catch(() => {
      toast.error("Failed to update order");
    });
  };

  const reorderYearLeaders = (
    yearKey: string,
    fromIndex: number,
    toIndex: number
  ) => {
    if (fromIndex === toIndex) return;

    const updatedYearWise = { ...yearWiseLeaders };
    const list = [...updatedYearWise[yearKey]];

    const [moved] = list.splice(fromIndex, 1);
    list.splice(toIndex, 0, moved);

    // update order_by
    const payload = list.map((item, index) => ({
      _id: item._id,
      order_by: index + 1,
    }));

    // update UI immediately
    updatedYearWise[yearKey] = list.map((l, i) => ({
      ...l,
      order_by: i + 1,
    }));

    setYearWiseLeaders(updatedYearWise);

    // save to backend
    WebService.postAPI({
      action: "leadership/update-order",
      body: payload,
    }).catch(() => {
      toast.error("Failed to update order");
    });
  };

  /* ================= DELETE ================= */
  const handleDeleteEvent = (id: string) => {
    setLeadershipCouncilToDelete(id);
    setShowDeleteModal(true);
  };

  return (
    <>
      <AddLeadershipCouncil
        show={showAddLeadershipCouncil}
        handleClose={() => {
          setShowAddLeadershipCouncil(false);
          setLeadershipCouncilToEdit(null);
          getLeadershipCouncils(searchRef.current);
          getLeadersByYear();
        }}
        data={leadershipCouncilToEdit}
      />

      <DeleteModal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        onDelete={() => {
          if (leadershipCouncilToDelete) {
            WebService.deleteAPI({
              action: `leadership/deleteLeader/${leadershipCouncilToDelete}`,
            }).then(() => {
              setShowDeleteModal(false);
              toast.success("Leadership member deleted successfully");
              getLeadershipCouncils(searchRef.current);
              getLeadersByYear();
            });
          }
        }}
      />

      <div className="events-main">
        <Card className="event-card">
          {/* PAGE HEADER */}
          <div className="d-flex justify-content-between align-items-center">
            <div className="page-title">Leadership Council</div>
            {pagePermission.canAdd && (
              <CustomButton
                label="Add"
                isLoading={false}
                onClick={() => {
                  setLeadershipCouncilToEdit(null);
                  setShowAddLeadershipCouncil(true);
                }}
                style={{ width: 100 }}
              />
            )}
          </div>

          <div className="row mt-3">
            <div className="col-md-3">
              <Form.Label className="label">Search</Form.Label>
              <Form.Control
                type="text"
                placeholder="Search name, designation, company"
                value={search}
                onChange={(e) => {
                  const value = e.target.value;
                  searchRef.current = value;                  
                  setSearch(value);
                  setCurrentPage(1);
                  getLeadershipCouncils(searchRef.current);
                }}
              />
            </div>
          </div>

          {/* ================= MAIN TABS ================= */}
          <Tabs defaultActiveKey="info" className="mt-3">

            {/* ================= INFO TAB ================= */}
            <Tab eventKey="info" title="Leaders">
              <div className="mt-3">
                <CustomTableSortable
                  columns={[
                    { id: "image", label: "Image", minWidth: 100, align: "center" },
                    { id: "name", label: "Name", minWidth: 170 },
                    { id: "designation", label: "Designation", minWidth: 150 },
                    { id: "company", label: "Company", minWidth: 150 },
                    { id: "year", label: "Year", minWidth: 100, align: "center" },
                    { id: "action", label: "Action", minWidth: 100, align: "center" },
                  ]}
                  rows={leadershipCouncils}
                  totalCount={totalCount}
                  currentPage={currentPage}
                  pageSize={itemsPerPage}
                  onPageChange={(page) => setCurrentPage(page)}
                  // draggable
                  // onRowsReorder={handleReorder}
                />
              </div>
            </Tab>

            {/* ================= YEAR TAB ================= */}
            <Tab eventKey="year" title="Year">
              <div className="mt-3">

                {yearKeys.length === 0 && (
                  <div className="text-muted">No year-wise data found</div>
                )}

                {yearKeys.length > 0 && (
                  <Tabs defaultActiveKey={yearKeys[0]} className="mt-2">
                    {yearKeys.map((yearKey) => (
                      <Tab eventKey={yearKey} title={yearKey} key={yearKey}>
                        <div className="d-flex flex-wrap gap-3 mt-3">

                          {(yearWiseLeaders[yearKey] || []).map((leader, index) => (
                            <div
                              key={leader._id}
                              draggable
                              onDragStart={(e) => {
                                e.dataTransfer.setData("fromIndex", index.toString());
                              }}
                              onDragOver={(e) => e.preventDefault()}
                              onDrop={(e) => {
                                const fromIndex = Number(
                                  e.dataTransfer.getData("fromIndex")
                                );
                                reorderYearLeaders(yearKey, fromIndex, index);
                              }}
                              className="border rounded p-3 d-flex flex-column align-items-center"
                              style={{
                                width: 180,
                                background: "#fff",
                                cursor: "move",
                              }}
                            >
                              {/* Drag handle */}
                              <span
                                style={{
                                  cursor: "grab",
                                  color: "#888",
                                  marginBottom: 6,
                                }}
                                title="Drag to reorder"
                              >
                                ⋮⋮
                              </span>

                              <img
                                src={leader.image}
                                alt={leader.name}
                                style={{
                                  width: 90,
                                  height: 90,
                                  borderRadius: "50%",
                                  objectFit: "cover",
                                }}
                              />

                              <div className="mt-2 text-center">
                                <strong>{leader.name}</strong>
                              </div>

                              <div
                                className="text-muted text-center"
                                style={{ fontSize: 13 }}
                              >
                                {leader.designation}
                              </div>
                            </div>
                          ))}

                        </div>
                      </Tab>
                    ))}
                  </Tabs>
                )}
              </div>
            </Tab>

          </Tabs>
        </Card>
      </div>
    </>
  );
};

export default LeadershipCouncil;
